import { SmartPanel } from '@/components/session/smart-panel';
import { Button } from '@/components/ui/button';
import { useTemplateDefaults } from '@/hooks/useTemplateDefaults';
import { useSaveUserSummary } from '@/lib/api';
import { apiRequest } from '@/lib/queryClient';
import type { SummaryStyle } from '@/types/summaryStyle';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { InterpreterRecord } from './api';
import type { InterpreterSummaryOptions, InterpreterSummaryResult } from './useInterpreter';

interface SavedSummary {
  summary_text: string;
  selected_template_id: number;
  generation_id?: string | null;
}
interface Props {
  record: InterpreterRecord;
  busy: boolean;
  onGenerate: (options: InterpreterSummaryOptions) => Promise<InterpreterSummaryResult | undefined>;
}
export const InterpreterSummaryPanel = ({ record, busy, onGenerate }: Props) => {
  const defaults = useTemplateDefaults();
  const [pickedTemplate, setPickedTemplate] = useState<number | null | undefined>();
  const [generated, setGenerated] = useState<InterpreterSummaryResult>();
  const [editedSummary, setEditedSummary] = useState<string | null>(null);
  const [savedText, setSavedText] = useState<string>();
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const context = useRef('');
  const inFlight = useRef(false);
  const save = useSaveUserSummary();
  const saved = useQuery({
    queryKey: ['interpreter-note', record.id],
    queryFn: async (): Promise<{ model_summary: SavedSummary | null; user_summary: SavedSummary | null }> =>
      (await apiRequest('GET', `/api/sessions/${record.id}/live-conversation`)).json(),
    gcTime: 0,
    retry: false,
  });
  const stored = saved.data?.user_summary ?? saved.data?.model_summary;
  const templateId =
    pickedTemplate === undefined ? (stored?.selected_template_id ?? defaults.conversation) : pickedTemplate;
  const summary = savedText ?? generated?.summary ?? stored?.summary_text ?? '';
  const complete = record.utterances.length > 0 && record.utterances.every((row) => row.translation_status === 'ready');
  const blocked = busy || save.isPending || saved.isPending || saved.isError || !complete;
  const transcript = record.utterances
    .map(
      (row) =>
        `${row.speaker === 'doctor' ? 'Læknir' : 'Sjúklingur'}: ${row.speaker === 'doctor' ? row.text : (row.translation ?? '')}`
    )
    .join('\n');
  const generate = async (style?: SummaryStyle) => {
    if (blocked || templateId == null || inFlight.current) return;
    inFlight.current = true;
    setGenerating(true);
    setError('');
    try {
      const result = await onGenerate({ template_id: templateId, style, pre_encounter_context: context.current });
      if (!result) return;
      setGenerated(result);
      setSavedText(undefined);
      setEditedSummary(null);
      if (!result.model_summary_saved)
        setError('Nótan var búin til en vistun tókst ekki. Vistaðu hana áður en þú yfirgefur síðuna.');
    } finally {
      inFlight.current = false;
      setGenerating(false);
    }
  };
  const persist = async (source: 'save' | 'copy', text: string) => {
    const noteTemplateId = generated ? generated.template_id : stored?.selected_template_id;
    if (noteTemplateId == null) {
      setError('Ekki tókst að finna sniðmát vistuðu nótunnar. Reyndu að sækja hana aftur.');
      return;
    }
    setError('');
    try {
      await save.mutateAsync({
        sessionId: record.id,
        summaryText: text,
        templateId: noteTemplateId,
        source,
        generationId: generated ? generated.generation_id : stored?.generation_id,
      });
      setSavedText(text);
      setEditedSummary(null);
    } catch {
      setError('Ekki tókst að vista nótuna. Breytingarnar eru enn hér.');
    }
  };
  return (
    <section aria-label="Klínísk nóta" className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-card">
      {(error || saved.isError) && (
        <p role="alert" className="bg-destructive/10 p-3 text-destructive text-sm">
          {error || 'Ekki tókst að sækja vistaða nótu.'}
          {saved.isError && (
            <Button variant="outline" className="ml-2" onClick={() => void saved.refetch()}>
              Sækja aftur
            </Button>
          )}
        </p>
      )}
      <SmartPanel
        summary={summary}
        documents={[]}
        isProcessing={false}
        dictationMode={false}
        onToggleDictation={() => {}}
        dictationText=""
        onDictationChange={() => {}}
        transcript={transcript}
        onGenerateSummary={(style) => void generate(style)}
        isGeneratingSummary={generating}
        isTranscriptionActive={blocked || saved.isPending}
        conversationTemplateId={templateId}
        onConversationTemplateChange={setPickedTemplate}
        editedSummary={editedSummary}
        onSummaryEdit={setEditedSummary}
        onSummarySave={() => void persist('save', editedSummary ?? summary)}
        isSavingSummary={save.isPending}
        onCopySummary={(text) => void persist('copy', text)}
        tasks={[]}
        confirmedTasks={new Set()}
        onToggleTaskCompletion={() => {}}
        onContextChange={(text) => {
          context.current = text;
        }}
      />
    </section>
  );
};
